import { getBundleParentIds, getSortedItems, isBundleParentRow } from './shelfUtils';
import type { OrderItem, ShelfLocation } from '../../../types/apiTypes';
import { SCAN_FAILURE_REASONS, type ScanFailureReason } from '@/shared/types/fulfillmentEvents.types';
import { QR_NOT_FOUND_RETRY_MESSAGE } from '../../scanner/qrScanMessages';

export const extractStorageIdFromQr = (qrData: string | null | undefined): string | null => {
  if (!qrData || typeof qrData !== 'string') return null;
  const cleaned = qrData.replace(/\s+/g, '').replace(/-/g, '').trim();
  const hexPart = cleaned.match(/^[0-9a-fA-F]+/);
  if (hexPart && hexPart[0].length >= 6) {
    return hexPart[0].substring(0, 6).toLowerCase();
  }
  return null;
};

export const extractSKUFromText = (ocrText: string): string => {
  return ocrText.replace(/\s+/g, '');
};

const norm = (sku: string): string => (sku || '').replace(/[\W_]+/g, '').toUpperCase();

/**
 * True when OCR/detected text matches an expected SKU, using the same flexible
 * character-order match as order-detail scan (noise between expected chars).
 */
export function ocrTextMatchesExpectedSku(ocrText: string, expectedSku: string): boolean {
  const expected = norm(expectedSku);
  const detected = norm(extractSKUFromText(ocrText));
  if (!expected || !detected) {
    return false;
  }
  const pattern = expected.split('').map((char) => `${char}.*?`).join('');
  return new RegExp(pattern).test(detected);
}

/**
 * Hardcoded sub-bundle: RSV-C-X-B-VUL-1-4-3-8 = 1x RSV-C-P-VUL-STANDARD + 1x RSV-1-4-3-8-CONVERTER.
 * This bundle is never shown as a parent; scanning it marks both components.
 * Only succeeds when exactly 1 of each is unscanned. Fails if only 1 component is unscanned.
 */
const SUB_BUNDLE_SKU = 'RSV-C-X-B-VUL-1-4-3-8';
const SUB_BUNDLE_COMPONENTS = ['RSV-C-P-VUL-STANDARD', 'RSV-1-4-3-8-CONVERTER'];

export interface BundleScanSubsetComponent {
  readonly sku: string;
  readonly quantity: number;
}

/** Normalized parent SKU → catalog BOM components for subset exception scanning. */
export type BundleScanSubsetBomMap = ReadonlyMap<string, readonly BundleScanSubsetComponent[]>;

const SUBSET_FAIL_MIX = 'Cannot scan components from bundle.';
const SUBSET_FAIL_CONSOLIDATED = 'Cannot use parent bundle scan on consolidated pick details';

type SubBundleScanResult = 'not_applicable' | 'matched' | 'failed' | 'continue';
type SubsetScanResult = 'not_applicable' | 'matched' | 'failed' | 'continue';

type PoolCandidate = {
  item: OrderItem;
  idx: number;
  isVisible: boolean;
};

type SourceTryResult =
  | { readonly kind: 'ok'; readonly indices: readonly number[] }
  | { readonly kind: 'consolidated' }
  | { readonly kind: 'insufficient' };

function expandBomMultiset(components: readonly BundleScanSubsetComponent[]): string[] {
  const required: string[] = [];
  for (const c of components) {
    const key = norm(c.sku);
    if (!key) continue;
    const qty = Number.isFinite(c.quantity) && c.quantity > 0 ? Math.floor(c.quantity) : 0;
    for (let i = 0; i < qty; i++) {
      required.push(key);
    }
  }
  return required;
}

function countBySku(skus: readonly string[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const sku of skus) {
    counts.set(sku, (counts.get(sku) ?? 0) + 1);
  }
  return counts;
}

function sortPoolCandidates(
  candidates: PoolCandidate[],
  displayIndexMap: Map<string, number>
): PoolCandidate[] {
  return [...candidates].sort((a, b) => {
    if (a.isVisible !== b.isVisible) return a.isVisible ? -1 : 1;
    return (displayIndexMap.get(a.item.id) ?? Infinity) - (displayIndexMap.get(b.item.id) ?? Infinity);
  });
}

/**
 * Try to allocate required SKU multiset from a closed pool without marking isAdjustedQty rows.
 * Returns consolidated when adjusted-qty rows are the only way to cover a shortfall.
 */
function tryAllocateFromPool(
  pool: PoolCandidate[],
  requiredMultiset: readonly string[],
  displayIndexMap: Map<string, number>
): SourceTryResult {
  const needed = countBySku(requiredMultiset);
  const sorted = sortPoolCandidates(pool, displayIndexMap);

  const nonAdjustedBySku = new Map<string, PoolCandidate[]>();
  const adjustedBySku = new Map<string, number>();

  for (const candidate of sorted) {
    const key = norm(candidate.item.sku);
    if (!key || !needed.has(key)) continue;
    if (candidate.item.isAdjustedQty) {
      adjustedBySku.set(key, (adjustedBySku.get(key) ?? 0) + 1);
      continue;
    }
    const list = nonAdjustedBySku.get(key) ?? [];
    list.push(candidate);
    nonAdjustedBySku.set(key, list);
  }

  const indices: number[] = [];
  let sawConsolidatedShortfall = false;

  for (const [sku, needCount] of needed) {
    const available = nonAdjustedBySku.get(sku) ?? [];
    if (available.length >= needCount) {
      for (let i = 0; i < needCount; i++) {
        indices.push(available[i].idx);
      }
      continue;
    }
    if ((adjustedBySku.get(sku) ?? 0) > 0) {
      sawConsolidatedShortfall = true;
    } else {
      return { kind: 'insufficient' };
    }
  }

  if (sawConsolidatedShortfall) {
    return { kind: 'consolidated' };
  }
  return { kind: 'ok', indices };
}

function autoCompleteParents(items: OrderItem[]): OrderItem[] {
  return items.map((item) => {
    if (item.isComponent || item.scanned) return item;
    const componentItems = items.filter(
      (compItem) => compItem.parentId === item.id && compItem.isComponent
    );
    if (componentItems.length === 0) return item;
    const allComponentsScanned = componentItems.every((compItem) => compItem.scanned);
    return allComponentsScanned ? { ...item, scanned: true } : item;
  });
}

function findAllowlistedParentSku(
  detectedSKU: string,
  subsetBomMap: BundleScanSubsetBomMap
): string | null {
  const detectedNorm = norm(detectedSKU);
  if (!detectedNorm) return null;
  for (const parentNorm of subsetBomMap.keys()) {
    if (parentNorm === detectedNorm) return parentNorm;
  }
  return null;
}

/** Build a scan-time BOM map keyed by normalized parent SKU. */
export function buildBundleScanSubsetBomMap(
  entries: ReadonlyArray<{
    readonly parentSku: string;
    readonly components: ReadonlyArray<BundleScanSubsetComponent>;
  }>
): Map<string, readonly BundleScanSubsetComponent[]> {
  const map = new Map<string, readonly BundleScanSubsetComponent[]>();
  for (const entry of entries) {
    const key = norm(entry.parentSku);
    if (!key) continue;
    map.set(key, entry.components);
  }
  return map;
}

/**
 * Exception path for allowlisted parent SKUs when no unscanned parent row exists.
 * Allocates component units from a single source: top-level standalones OR one other parent.
 */
function tryBundleScanSubset(
  detectedSKU: string,
  orderItems: OrderItem[],
  setOrderItems: (items: OrderItem[]) => void,
  setMessage: (msg: string) => void,
  visibleItems: Set<string>,
  displayIndexMap: Map<string, number>,
  subsetBomMap: BundleScanSubsetBomMap,
  floorParentSku?: string,
): SubsetScanResult {
  if (subsetBomMap.size === 0) return 'not_applicable';

  const parentNorm = findAllowlistedParentSku(detectedSKU, subsetBomMap);
  if (!parentNorm) return 'not_applicable';

  const floorParentNorm = floorParentSku ? norm(floorParentSku) : null;
  const hasUnpickedParent = floorParentNorm !== parentNorm && orderItems.some(
    (item) =>
      item.isBundleParent &&
      !item.scanned &&
      norm(item.sku) === parentNorm
  );
  if (hasUnpickedParent) {
    return 'continue';
  }

  const components = subsetBomMap.get(parentNorm);
  if (!components || components.length === 0) {
    setMessage(SUBSET_FAIL_MIX);
    return 'failed';
  }

  const requiredMultiset = expandBomMultiset(components);
  if (requiredMultiset.length === 0) {
    setMessage(SUBSET_FAIL_MIX);
    return 'failed';
  }

  const withMeta = orderItems.map((item, idx) => ({
    item,
    idx,
    isVisible: visibleItems.has(item.id),
  }));

  const topLevelPool = withMeta.filter(
    ({ item }) =>
      !item.scanned &&
      !item.isComponent &&
      !item.isBundleParent
  );

  const sources: PoolCandidate[][] = [topLevelPool];

  const parentIdsInDisplayOrder: string[] = [];
  const seenParentIds = new Set<string>();
  for (const sortedItem of getSortedItems(orderItems)) {
    if (!sortedItem.isComponent || !sortedItem.parentId) continue;
    if (seenParentIds.has(sortedItem.parentId)) continue;
    const parentRow = orderItems.find((i) => i.id === sortedItem.parentId);
    // Skip donor pools that belong to the allowlisted parent itself (already scanned instances).
    if (parentRow && norm(parentRow.sku) === parentNorm) continue;
    seenParentIds.add(sortedItem.parentId);
    parentIdsInDisplayOrder.push(sortedItem.parentId);
  }

  for (const parentId of parentIdsInDisplayOrder) {
    sources.push(
      withMeta.filter(
        ({ item }) =>
          !item.scanned &&
          item.isComponent === true &&
          item.parentId === parentId
      )
    );
  }

  let sawConsolidated = false;
  for (const pool of sources) {
    if (pool.length === 0) continue;
    const result = tryAllocateFromPool(pool, requiredMultiset, displayIndexMap);
    if (result.kind === 'ok') {
      const indicesToMark = new Set(result.indices);
      let updatedItems = orderItems.map((orig, idx) =>
        indicesToMark.has(idx) ? { ...orig, scanned: true } : orig
      );
      updatedItems = autoCompleteParents(updatedItems);
      setOrderItems(updatedItems);
      setMessage(`SKU ${detectedSKU} matched (bundle scan subset).`);
      return 'matched';
    }
    if (result.kind === 'consolidated') {
      sawConsolidated = true;
    }
  }

  setMessage(sawConsolidated ? SUBSET_FAIL_CONSOLIDATED : SUBSET_FAIL_MIX);
  return 'failed';
}

function trySubBundleScan(
  detectedSKU: string,
  orderItems: OrderItem[],
  setOrderItems: (items: OrderItem[]) => void,
  setMessage: (msg: string) => void,
  visibleItems: Set<string>,
  displayIndexMap: Map<string, number>
): SubBundleScanResult {
  if (norm(detectedSKU) !== norm(SUB_BUNDLE_SKU)) return 'not_applicable';

  const unscanned = orderItems
    .map((item, idx) => ({ item, idx }))
    .filter(({ item }) => !item.scanned && SUB_BUNDLE_COMPONENTS.some((c) => norm(item.sku) === norm(c)));

  const bySku: Record<string, { item: OrderItem; idx: number; isVisible: boolean }[]> = {};
  unscanned.forEach(({ item, idx }) => {
    const key = norm(item.sku);
    if (!bySku[key]) bySku[key] = [];
    bySku[key].push({ item, idx, isVisible: visibleItems.has(item.id) });
  });

  const standard = bySku[norm(SUB_BUNDLE_COMPONENTS[0])] || [];
  const converter = bySku[norm(SUB_BUNDLE_COMPONENTS[1])] || [];

  if (standard.length === 1 && converter.length === 1) {
    const picks = [standard[0], converter[0]].sort((a, b) => {
      if (a.isVisible !== b.isVisible) return a.isVisible ? -1 : 1;
      return (displayIndexMap?.get(a.item.id) ?? Infinity) - (displayIndexMap?.get(b.item.id) ?? Infinity);
    });
    const indicesToMark = new Set(picks.map((p) => p.idx));
    const updatedItems = orderItems.map((orig, idx) =>
      indicesToMark.has(idx) ? { ...orig, scanned: true } : orig
    );
    setOrderItems(updatedItems);
    setMessage(`SKU ${detectedSKU} matched (sub-bundle: both components).`);
    return 'matched';
  }

  if (standard.length > 0 || converter.length > 0) {
    setMessage(`Scan failed: ${SUB_BUNDLE_SKU} requires 1 unscanned of each component.`);
    return 'failed';
  }

  return 'continue';
}

function buildDisplayIndexMap(orderItems: OrderItem[]): Map<string, number> {
  const sorted = getSortedItems(orderItems);
  const map = new Map<string, number>();
  sorted.forEach((item: OrderItem, i: number) => map.set(item.id, i));
  return map;
}

interface PotentialMatch {
  index: number;
  item: OrderItem;
  skuLength: number;
  isVisible: boolean;
}

function collectPotentialMatches(
  orderItems: OrderItem[],
  detectedSKU: string,
  visibleItems: Set<string>,
  exactMatch: boolean,
  includeScannedPickLines: boolean,
  parentIds: Set<string>,
): PotentialMatch[] {
  const normalizedDetectedSKU = norm(detectedSKU);
  const potentialMatches: PotentialMatch[] = [];

  for (let i = 0; i < orderItems.length; i++) {
    const item = orderItems[i];

    if (item.scanned) {
      if (!(includeScannedPickLines && !isBundleParentRow(item, parentIds))) {
        continue;
      }
    }

    const itemSKU = (item.sku || '').replace(/[\W_]+/g, '').toUpperCase();
    const matches = exactMatch
      ? itemSKU === normalizedDetectedSKU
      : (() => {
          const pattern = itemSKU.split('').map((char: string) => `${char}.*?`).join('');
          return new RegExp(pattern).test(normalizedDetectedSKU);
        })();

    if (matches) {
      const isVisible = visibleItems.has(item.id);
      potentialMatches.push({ index: i, item, skuLength: itemSKU.length, isVisible });
    }
  }

  return potentialMatches;
}

/** Constrain a capture to one pick-line SKU (shelf-index click while camera is open). */
export interface TargetedScan {
  readonly expectedSku: string;
  readonly preferItemId?: string;
}

export function targetedScanFromPickItem(item: OrderItem): TargetedScan | null {
  if (!item.sku) return null;
  return {
    expectedSku: item.sku,
    preferItemId: item.firstItemId ?? item.id,
  };
}

function sortPotentialMatches(
  potentialMatches: PotentialMatch[],
  normalizedDetectedSKU: string,
  displayIndexMap: Map<string, number>,
  preferItemId?: string,
): PotentialMatch[] {
  return [...potentialMatches].sort((a, b) => {
    if (preferItemId) {
      const aPreferred = a.item.id === preferItemId;
      const bPreferred = b.item.id === preferItemId;
      if (aPreferred !== bPreferred) return aPreferred ? -1 : 1;
    }
    if (a.isVisible !== b.isVisible) {
      return a.isVisible ? -1 : 1;
    }
    if (b.skuLength !== a.skuLength) {
      return b.skuLength - a.skuLength;
    }
    const aNorm = (a.item.sku || '').replace(/[\W_]+/g, '').toUpperCase();
    const bNorm = (b.item.sku || '').replace(/[\W_]+/g, '').toUpperCase();
    if (aNorm === normalizedDetectedSKU && bNorm !== normalizedDetectedSKU) return -1;
    if (bNorm === normalizedDetectedSKU && aNorm !== normalizedDetectedSKU) return 1;
    if (aNorm === normalizedDetectedSKU && bNorm === normalizedDetectedSKU) {
      return (a.item.isComponent ? 1 : 0) - (b.item.isComponent ? 1 : 0);
    }
    return (displayIndexMap.get(a.item.id) ?? Infinity) - (displayIndexMap.get(b.item.id) ?? Infinity);
  });
}

function applySkuScanPulse(
  orderItems: OrderItem[],
  matchedSkuNorm: string,
  markScanned: boolean,
  parentIds: Set<string>,
): OrderItem[] {
  const pulse = Date.now();
  return orderItems.map((orig) => {
    if (!isBundleParentRow(orig, parentIds) && norm(orig.sku) === matchedSkuNorm) {
      return markScanned
        ? { ...orig, scanned: true, scanPulse: pulse }
        : { ...orig, scanPulse: pulse };
    }
    return orig;
  });
}

/** Warehouse label resolved from a successful QR that flipped at least one pick line. */
export interface ScannedLabel {
  readonly storageIdPrefix: string;
  readonly sku: string;
  readonly shelfIndex: string | null;
}

/** Result of a scan attempt: whether a line matched, and the canonical SKU of the matched line. */
export interface ScanMatchResult {
  readonly matched: boolean;
  readonly matchedSku: string | null;
  readonly bundleParentFloorDecrement?: {
    readonly parentSku: string;
    readonly storageIdPrefix?: string;
    /**
     * One pick line id per bundle this scan took off the floor. Empty when the scan
     * changed nothing (rescan of an already-satisfied line), so no quantity moves.
     */
    readonly itemIds: readonly string[];
  };
  /** Present on a QR match that newly marked at least one pick line. */
  readonly scannedLabel?: ScannedLabel;
  readonly failure?: {
    readonly reason: ScanFailureReason;
    readonly ocrText: string | null;
  };
}

function noMatch(failure: ScanMatchResult['failure']): ScanMatchResult {
  return { matched: false, matchedSku: null, failure };
}

const NO_MATCH: ScanMatchResult = { matched: false, matchedSku: null };

export const matchSKU = (
  detectedSKU: string,
  orderItems: OrderItem[],
  setOrderItems: (items: OrderItem[]) => void,
  setMessage: (msg: string) => void,
  visibleItems: Set<string> = new Set(),
  componentsOnlyMode: boolean = false,
  exactMatch: boolean = false,
  subsetBomMap: BundleScanSubsetBomMap = new Map(),
  floorParentSku?: string,
  preferItemId?: string,
): ScanMatchResult => {
  const displayIndexMap = buildDisplayIndexMap(orderItems);
  // Inner scan paths own their own state writes; capture the result so the floor decrement
  // can be derived from what actually flipped to scanned.
  let appliedItems: readonly OrderItem[] = orderItems;
  const applyOrderItems = (items: OrderItem[]): void => {
    appliedItems = items;
    setOrderItems(items);
  };

  if (!exactMatch) {
    const subResult = trySubBundleScan(detectedSKU, orderItems, applyOrderItems, setMessage, visibleItems, displayIndexMap);
    if (subResult === 'matched') return { matched: true, matchedSku: SUB_BUNDLE_SKU };
    if (subResult === 'failed') {
      return noMatch({ reason: SCAN_FAILURE_REASONS.NO_MATCH, ocrText: detectedSKU });
    }
  }

  const subsetResult = tryBundleScanSubset(
    detectedSKU,
    orderItems,
    applyOrderItems,
    setMessage,
    visibleItems,
    displayIndexMap,
    subsetBomMap,
    floorParentSku,
  );
  if (subsetResult === 'matched') {
    const parentNorm = findAllowlistedParentSku(detectedSKU, subsetBomMap);
    const parentRowSku = orderItems.find(
      (i) => i.isBundleParent && parentNorm != null && norm(i.sku) === parentNorm
    )?.sku
      ?? (floorParentSku && parentNorm != null && norm(floorParentSku) === parentNorm ? floorParentSku : null)
      ?? detectedSKU;
    return withBundleParentFloorDecrement(
      { matched: true, matchedSku: parentRowSku },
      orderItems,
      appliedItems,
      floorParentSku,
    );
  }
  if (subsetResult === 'failed') {
    return noMatch({ reason: SCAN_FAILURE_REASONS.NO_MATCH, ocrText: detectedSKU });
  }

  let found = false;
  let matchedSku: string | null = null;
  let updatedItems = [...orderItems];
  let isPickLineRescan = false;
  const parentIds = getBundleParentIds(updatedItems);

  const normalizedDetectedSKU = norm(detectedSKU);

  let potentialMatches = collectPotentialMatches(
    updatedItems,
    detectedSKU,
    visibleItems,
    exactMatch,
    false,
    parentIds,
  );
  if (potentialMatches.length === 0 && componentsOnlyMode) {
    potentialMatches = collectPotentialMatches(
      updatedItems,
      detectedSKU,
      visibleItems,
      exactMatch,
      true,
      parentIds,
    );
    isPickLineRescan = potentialMatches.length > 0;
  }

  if (potentialMatches.length > 0) {
    found = true;

    potentialMatches = sortPotentialMatches(
      potentialMatches,
      normalizedDetectedSKU,
      displayIndexMap,
      preferItemId,
    );

    const bestMatch = potentialMatches[0];
    const matchedIndex = bestMatch.index;
    const item = bestMatch.item;
    matchedSku = item.sku || null;

    if (componentsOnlyMode && !isBundleParentRow(item, parentIds)) {
      const matchedSkuNorm = norm(item.sku);
      updatedItems = applySkuScanPulse(updatedItems, matchedSkuNorm, !isPickLineRescan, parentIds);
    } else if (!item.isComponent) {
      const parentId = item.id;
      const componentIndices: number[] = [];
      updatedItems.forEach((compItem, idx) => {
        if (compItem.parentId === parentId && compItem.isComponent) {
          componentIndices.push(idx);
        }
      });
      const indicesToMark = new Set([matchedIndex, ...componentIndices]);
      updatedItems = updatedItems.map((orig, idx) =>
        indicesToMark.has(idx) ? { ...orig, scanned: true } : orig
      );
    } else {
      updatedItems[matchedIndex] = { ...item, scanned: true };
      if (item.parentId) {
        const parentBundle = updatedItems.find(
          (bundleItem) => bundleItem.id === item.parentId
        );
        const siblingComponents = updatedItems.filter(
          (compItem) =>
            compItem.parentId === item.parentId &&
            compItem.isComponent &&
            compItem.id !== item.id
        );
        const allSiblingsScanned = siblingComponents.every(
          (compItem) => compItem.scanned
        );
        if (allSiblingsScanned && parentBundle) {
          const parentIdx = updatedItems.findIndex(
            (bundleItem) => bundleItem.id === item.parentId
          );
          if (parentIdx >= 0) {
            updatedItems = [...updatedItems];
            updatedItems[parentIdx] = { ...parentBundle, scanned: true };
          }
        }
      }
    }
  }

  updatedItems = updatedItems.map((item) => {
    if (item.isComponent || item.scanned) return item;
    const componentItems = updatedItems.filter(
      (compItem) => compItem.parentId === item.id && compItem.isComponent
    );
    if (componentItems.length === 0) return item;
    const allComponentsScanned = componentItems.every(
      (compItem) => compItem.scanned
    );
    return allComponentsScanned ? { ...item, scanned: true } : item;
  });

  applyOrderItems(updatedItems);
  setMessage(
    found
      ? `SKU ${detectedSKU} matched.`
      : `SKU ${detectedSKU} not found or already scanned.`
  );
  const baseResult = found
    ? { matched: true, matchedSku }
    : noMatch({ reason: SCAN_FAILURE_REASONS.NO_MATCH, ocrText: detectedSKU });
  return withBundleParentFloorDecrement(baseResult, orderItems, appliedItems, floorParentSku);
};

export const handleCapture = async (
  imageSrc: string,
  performOCR: (img: string) => Promise<string | null>,
  orderItems: OrderItem[],
  setOrderItems: (items: OrderItem[]) => void,
  setMessage: (msg: string) => void,
  visibleItems: Set<string> = new Set(),
  componentsOnlyMode: boolean = false,
  subsetBomMap: BundleScanSubsetBomMap = new Map(),
  floorParentSku?: string,
  target?: TargetedScan,
): Promise<ScanMatchResult> => {
  setMessage('Processing image...');
  const base64Image = imageSrc;
  const ocrText = await performOCR(base64Image);

  if (ocrText) {
    const detectedSKU = extractSKUFromText(ocrText);
    if (target) {
      if (!ocrTextMatchesExpectedSku(ocrText, target.expectedSku)) {
        setMessage(`SKU did not match ${target.expectedSku}. Scan input: ${ocrText}`);
        return noMatch({ reason: SCAN_FAILURE_REASONS.NO_MATCH, ocrText: detectedSKU || null });
      }
      return matchSKU(
        target.expectedSku,
        orderItems,
        setOrderItems,
        setMessage,
        visibleItems,
        componentsOnlyMode,
        true,
        subsetBomMap,
        floorParentSku,
        target.preferItemId,
      );
    }
    if (detectedSKU) {
      return matchSKU(
        detectedSKU,
        orderItems,
        setOrderItems,
        setMessage,
        visibleItems,
        componentsOnlyMode,
        false,
        subsetBomMap,
        floorParentSku,
      );
    }
    setMessage('No SKU detected. Please try again.');
    return noMatch({ reason: SCAN_FAILURE_REASONS.NO_SKU_DETECTED, ocrText: null });
  }
  setMessage('Error processing image. Please try again.');
  return noMatch({ reason: SCAN_FAILURE_REASONS.OCR_ERROR, ocrText: null });
};

const PICK_BIN_TYPE = 'pick bin';

/** Bundle parent SKU on this order when the scanned SKU resolves to a tracked parent row. */
export function resolveBundleParentSkuForFloorDecrement(
  sku: string,
  orderItems: OrderItem[],
  floorParentSku?: string,
): string | null {
  const normalized = norm(sku);
  if (floorParentSku && norm(floorParentSku) === normalized) {
    return floorParentSku;
  }
  const parent = orderItems.find(
    (item) => item.isBundleParent && norm(item.sku) === normalized,
  );
  return parent?.sku ?? null;
}

function isPickBinLabelType(type: string | null | undefined): boolean {
  return String(type ?? '').toLowerCase().trim() === PICK_BIN_TYPE;
}

function pickLinesNewlyScanned(
  before: readonly OrderItem[],
  after: readonly OrderItem[],
): boolean {
  const scannedBefore = new Set(before.filter((item) => item.scanned).map((item) => item.id));
  return after.some((item) => item.scanned && !scannedBefore.has(item.id));
}

/**
 * One line id per bundle that just left the floor. A parent pick line is one bundle each;
 * with no parent line on the order (bundle work orders, component-only subset matches) a
 * match consumes a single assembled bundle, keyed on the first line it satisfied.
 */
function bundleFloorDecrementItemIds(
  before: readonly OrderItem[],
  after: readonly OrderItem[],
  parentSku: string,
): string[] {
  const scannedBefore = new Set(before.filter((item) => item.scanned).map((item) => item.id));
  const newlyScanned = after.filter((item) => item.scanned && !scannedBefore.has(item.id));
  if (newlyScanned.length === 0) {
    return [];
  }
  const parentSkuNorm = norm(parentSku);
  const parentLines = newlyScanned.filter(
    (item) => item.isBundleParent && norm(item.sku) === parentSkuNorm,
  );
  return parentLines.length > 0
    ? parentLines.map((item) => item.id)
    : [newlyScanned[0].id];
}

function withBundleParentFloorDecrement(
  result: ScanMatchResult,
  orderItems: OrderItem[],
  updatedItems: readonly OrderItem[],
  floorParentSku?: string,
): ScanMatchResult {
  if (!result.matched) {
    return result;
  }
  const parentSku = resolveBundleParentSkuForFloorDecrement(
    result.matchedSku ?? '',
    orderItems,
    floorParentSku,
  );
  if (!parentSku) {
    return result;
  }
  const itemIds = bundleFloorDecrementItemIds(orderItems, updatedItems, parentSku);
  if (itemIds.length === 0) {
    return result;
  }
  return {
    ...result,
    bundleParentFloorDecrement: { parentSku, itemIds },
  };
}

/**
 * Attributes a pending decrement to the pick bin that was scanned. Without a bin the server
 * falls back to the parent's primary pick bin.
 */
function withFloorDecrementBin(
  result: ScanMatchResult,
  storageIdPrefix: string,
): ScanMatchResult {
  const decrement = result.bundleParentFloorDecrement;
  if (!decrement) {
    return result;
  }
  return {
    ...result,
    bundleParentFloorDecrement: { ...decrement, storageIdPrefix },
  };
}

export const buildPrefixToSkuMap = async (
  orderSkus: string[],
  searchSkuLocations: (sku: string) => Promise<{ locations: ShelfLocation[] }>
): Promise<Record<string, string>> => {
  const uniqueSkus = [...new Set((orderSkus || []).filter(Boolean).map((s) => String(s).trim().toUpperCase()))];
  const map: Record<string, string> = {};
  await Promise.all(
    uniqueSkus.map(async (sku) => {
      try {
        const { locations = [] } = await searchSkuLocations(sku);
        locations.forEach((loc: ShelfLocation) => {
          const typeNorm = String(loc.type || '').toLowerCase().trim();
          if (loc.id && loc.id.length >= 6 && typeNorm === PICK_BIN_TYPE) {
            const p = loc.id.substring(0, 6).toLowerCase();
            map[p] = sku;
          }
        });
      } catch {
        // ignore per-SKU errors
      }
    })
  );
  return map;
};

export const handleQrCapture = async (
  qrData: string,
  prefixToSkuMap: Record<string, string>,
  getLabelStatus: (prefix: string) => Promise<{ found: boolean; checkedIn: boolean }>,
  getLabelByStorageIdPrefix: (prefix: string) => Promise<{
    sku?: string;
    type?: string | null;
    shelfIndex?: string | null;
  } | null>,
  orderItems: OrderItem[],
  setOrderItems: (items: OrderItem[]) => void,
  setMessage: (msg: string) => void,
  visibleItems: Set<string> = new Set(),
  componentsOnlyMode: boolean = false,
  subsetBomMap: BundleScanSubsetBomMap = new Map(),
  floorParentSku?: string,
  target?: TargetedScan,
): Promise<ScanMatchResult> => {
  const prefix = extractStorageIdFromQr(qrData);
  if (!prefix) {
    setMessage(QR_NOT_FOUND_RETRY_MESSAGE);
    return NO_MATCH;
  }

  let sku: string | null = null;
  let isPickBinScan = false;
  let lookedUpShelfIndex: string | null = null;

  if (prefixToSkuMap && prefixToSkuMap[prefix]) {
    sku = prefixToSkuMap[prefix];
    isPickBinScan = true;
  }

  if (!sku) {
    setMessage('Looking up label...');
    const status = await getLabelStatus(prefix);
    if (status.found && !status.checkedIn) {
      setMessage('Storage ID is checked out');
      return NO_MATCH;
    }
    if (!status.found) {
      setMessage('Label not found in database or not in this order.');
      return NO_MATCH;
    }
    const label = await getLabelByStorageIdPrefix(prefix);
    sku = label?.sku || null;
    lookedUpShelfIndex = label?.shelfIndex ?? null;
    if (isPickBinLabelType(label?.type)) {
      isPickBinScan = true;
    }
  }

  if (sku) {
    if (target && norm(sku) !== norm(target.expectedSku)) {
      setMessage(`Bin does not contain ${target.expectedSku}.`);
      return NO_MATCH;
    }
    let afterItems: readonly OrderItem[] = orderItems;
    const captureSetOrderItems = (items: OrderItem[]): void => {
      afterItems = items;
      setOrderItems(items);
    };
    const result = await matchSKU(
      target?.expectedSku ?? sku,
      orderItems,
      captureSetOrderItems,
      setMessage,
      visibleItems,
      componentsOnlyMode,
      true,
      subsetBomMap,
      floorParentSku,
      target?.preferItemId,
    );
    const withBin = isPickBinScan ? withFloorDecrementBin(result, prefix) : result;
    if (!withBin.matched || !pickLinesNewlyScanned(orderItems, afterItems)) {
      return withBin;
    }
    return {
      ...withBin,
      scannedLabel: {
        storageIdPrefix: prefix,
        sku,
        shelfIndex: lookedUpShelfIndex,
      },
    };
  }
  setMessage('Label not found in database or not in this order.');
  return NO_MATCH;
};
